import { type VincoloCardinalitaDTO } from '@framework/api-client/api/inquiries/schemas/vincoloCardinalitaDTO.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { BannerCardinalityWarning } from '@framework/common-web/schema/submissions/cardinality/BannerCardinalityWarning.tsx';
import { CardinalityConstraintContextProvider } from '@framework/common-web/schema/submissions/cardinality/CardinalityConstraintContext.tsx';
import { isInquiryReadonlyPreview } from '@framework/common-web/ui/inquiry/isInquiryReadonlyPreview.ts';
import { InquiryTypeStateTag } from '@framework/common-web/ui/inquiry/state/InquiryTypeStateTag.tsx';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { EngineThemeProvider } from '@framework/engine/ui/theme/EngineThemeProvider.tsx';
import { FormCoordinator } from '@framework/form/form/coordinator/FormCoordinatorContext.tsx';
import { DelegationContextProvider } from '@framework/frontoffice-submission/module/delegation/context/DelegationContext.tsx';
import {
  DelegationMachine,
  isDelegate,
} from '@framework/frontoffice-submission/module/delegation/DelegationMachine.ts';
import { ModuleFormSectionsMonitorContextProvider } from '@framework/frontoffice-submission/module/form/sections-monitor/ModuleFormSectionsMonitorContext.tsx';
import { useSubmissionNavigation } from '@framework/frontoffice-submission/navigation/useSubmissionNavigation.ts';
import { SubmissionPaymentsCard } from '@framework/frontoffice-submission/payments/SubmissionPaymentsCard.tsx';
import { FlexSubmissionRow } from '@framework/frontoffice-submission/submission/FlexSubmissionRow.tsx';
import { useSubmission } from '@framework/frontoffice-submission/submission/hooks/useSubmission.ts';
import { useSubmissionMenuItems } from '@framework/frontoffice-submission/submission/hooks/useSubmissionMenuItems.tsx';
import { useSubmissionModules } from '@framework/frontoffice-submission/submission/hooks/useSubmissionModules.ts';
import { SubmissionMenu } from '@framework/frontoffice-submission/submission/menu/SubmissionMenu.tsx';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import { SubmissionStateTag } from '@framework/frontoffice-submission/submission/state/SubmissionStateTag.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  type BrandedPath,
  generatePath,
  Route,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Space } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps, Switch } from 'react-router';
import { useTheme } from 'styled-components';
import { CompilationGuideCard } from '../../components/submissions/cards/compilation-guide/CompilationGuideCard.tsx';
import { CompilationModuleCard } from '../../components/submissions/cards/module/CompilationModuleCard.tsx';
import { CompilationSendCard } from '../../components/submissions/cards/send/CompilationSendCard.tsx';
import { getSubmissionMenuLink } from '../../components/submissions/hooks/getSubmissionMenuLink.ts';
import { PageLayout } from '../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../loading/LoadingPage.tsx';
import { ContentCardWrapper } from './components/ContentCardWrapper.tsx';
import { InPaymentBanner } from './components/InPaymentBanner.tsx';
import { SubmissionPrincipalModuleRedirector } from './components/principal-module-redirector/SubmissionPrincipalModuleRedirector.tsx';
import { SubmissionDelegationBanner } from './components/SubmissionDelegationBanner.tsx';
import { SubmissionDelegationButton } from './components/SubmissionDelegationButton.tsx';
import { SubmissionCloneButton } from './components/SubmissionDuplicateButton.tsx';
import { SubmissionLinkedBox } from './components/SubmissionLinkedBox.tsx';
import { DelegationInfoTooltip } from './delegation/components/DelegationInfoTooltip.tsx';
import { useDelegationData } from './delegation/hooks/useDelegationData.ts';
import { IntegrationBanner } from './sent/IntegrationBanner.tsx';

interface Params {
  id: string;
  moduleId: string;
}

type ISubmissionPageProps = RouteComponentProps<Params>;

export function SubmissionPage(props: ISubmissionPageProps) {
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId');
  const history = useHistory();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const theme = useTheme();

  const { submission, inquiry, title, submissionRequest, inquiryRequest } =
    useSubmission(submissionId);

  const submissionModules = useSubmissionModules({ submissionId });

  const hasIntegrations = SubmissionLogic.hasIntegrations(submission);

  const [cardinalityConstraints, setCardinalityConstraints] = useState<
    VincoloCardinalitaDTO[] | undefined | null
  >([]);

  const {
    delegationData,
    isLoading,
    error: delegationError,
  } = useDelegationData({
    modules: submissionModules.modules,
    applicant: submission?.datiTitolari,
    submissionId,
  });
  const canViewDelegates = DelegationMachine.can(
    delegationData,
    '.view',
    currentUser,
  );

  const canSubmit = DelegationMachine.can(
    delegationData,
    '.canSubmit',
    currentUser,
  );

  const isReadonlyPreview = isInquiryReadonlyPreview(inquiry);

  const { menuItems: submissionMenuItems } = useSubmissionMenuItems({
    modules: submissionModules.modules,
    submissionId,
    submission,
    getMenuLink: getSubmissionMenuLink,
    // In anteprima di sola lettura l'invio non è possibile:
    // il menu mostra "Riepilogo" e la voce Pagamenti è nascosta
    canSubmit: canSubmit.allowed && !isReadonlyPreview,
    hidePayment: isReadonlyPreview,
  });

  const handleNavigation = useSubmissionNavigation({
    submissionId,
    steps: submissionMenuItems,
  });

  // Breadcrumbs
  useBreadcrumbItem({
    path: '/richieste',
    title: t('submission-list.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: props.location.pathname as BrandedPath,
    title,
  });

  // Redirect alla pagina di processamento (in invio)
  if (SubmissionLogic.isSubmissionPending(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/risultato', { id: submissionId }),
    );
  }

  if (SubmissionLogic.isSubmissionUndo(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/inviata', { id: submissionId }),
    );
  }

  if (SubmissionLogic.isSubmissionSent(submission) && submissionId) {
    history.replace(
      generatePath('/richieste/:id/inviata', { id: submissionId }),
    );
  }

  // Controllo se l'utente è delegato
  const isSubmissionDelegate = useMemo(() => {
    if (isLoading) return false;
    return isDelegate(delegationData, currentUser);
  }, [currentUser, delegationData, isLoading]);

  const isInPayment = SubmissionLogic.isSubmissionInPayment(submission);

  const showDelegationButton = SubmissionLogic.canViewDelegationPage({
    submission,
    canViewDelegates: canViewDelegates.allowed,
    inquiryType: inquiry,
  });

  const showCloneButton =
    !inquiryRequest.data?.flRichiestaUnivoca &&
    DelegationMachine.can(delegationData, '.canDuplicate', currentUser)
      .allowed &&
    !isReadonlyPreview;

  if (
    submissionModules.isLoading ||
    submissionRequest.isLoading ||
    inquiryRequest.isLoading ||
    isLoading
  ) {
    return <LoadingPage />;
  }

  if (
    submissionModules.isError ||
    submissionRequest.isError ||
    inquiryRequest.isError ||
    // In caso di delegati, gli endpoint di delega non sono autorizzati
    // e tornano 403, ma non è un errore da mostrare all'utente
    // In ogni caso le visibilità sono comunque gestite per gli altri endpoint
    (delegationError && delegationError?.response?.status !== 403)
  ) {
    return (
      <NetworkAlertPage
        error={
          submissionRequest.error ??
          inquiryRequest.error ??
          submissionModules.error ??
          delegationError
        }
        message={t(
          'submission-form.errore-in-caricamento-domanda',
          'Si è verificato un errore durante il caricamento della domanda.',
        )}
      />
    );
  }

  return (
    <PageLayout>
      <DelegationContextProvider delegationData={delegationData}>
        <CardinalityConstraintContextProvider
          constraints={cardinalityConstraints}
          setConstraints={setCardinalityConstraints}
        >
          <SubmissionLinkedBox submission={submission} inquiry={inquiry} />
          <ContentCardWrapper submission={submission}>
            <ResponsiveCard
              hideCardHeader={theme.searchButtonOnTableFlag}
              title={
                <TitleSubmissionRow
                  stateTag={
                    inquiry && isReadonlyPreview ? (
                      <InquiryTypeStateTag inquiry={inquiry} />
                    ) : (
                      <Space>
                        <SubmissionStateTag submission={submission} />
                        <DelegationInfoTooltip
                          delegationData={delegationData}
                        />
                      </Space>
                    )
                  }
                  right={
                    <Space>
                      {showCloneButton && (
                        <SubmissionCloneButton
                          isJustCreated={
                            submission?.stato === EnumRichiestaDTOStato.CREATA
                          }
                          submissionId={submissionId}
                        />
                      )}
                      {showDelegationButton && (
                        <SubmissionDelegationButton submission={submission} />
                      )}
                    </Space>
                  }
                >
                  {title}
                </TitleSubmissionRow>
              }
              cover={
                <>
                  {isInPayment && (
                    <InPaymentBanner
                      submissionId={submissionId!}
                      isPaymentCancelable={submission?.flagPagamentoAnnullabile}
                    />
                  )}
                  {isSubmissionDelegate ? <SubmissionDelegationBanner /> : null}
                  {hasIntegrations && (
                    <IntegrationBanner
                      integrationID={null}
                      message={t(
                        'submission-form.integrazione-di-un-altra-richiesta',
                        "Questa è un'integrazione di un'altra richiesta.",
                      )}
                      originalRequestId={submission?.richiestaOriginale?.id}
                    />
                  )}
                  <BannerCardinalityWarning submission={submission} />
                </>
              }
            >
              <FormCoordinator>
                <ModuleFormSectionsMonitorContextProvider>
                  <FlexSubmissionRow
                    menuSide="right"
                    menu={
                      <SubmissionMenu
                        items={submissionMenuItems}
                        currentSubmissionModuleId={
                          submissionModuleId ?? '<unknown>'
                        }
                      />
                    }
                    mobileRules={{ showBefore: true }}
                  >
                    <EngineThemeProvider>
                      <Switch>
                        <Route
                          exact
                          path="/richieste/:id"
                          render={(props: any) => (
                            <CompilationGuideCard
                              {...props}
                              handleNavigation={handleNavigation}
                            />
                          )}
                        />
                        <Route
                          exact
                          path="/richieste/:id/moduli/:moduleId"
                          render={(props: any) => (
                            <CompilationModuleCard
                              {...props}
                              readonly={
                                isReadonlyPreview
                                  ? true
                                  : // Non possiamo ritornare false, altrimenti sovrascriviamo
                                    // la gestione interna di readonly
                                    undefined
                              }
                              handleNavigation={handleNavigation}
                            />
                          )}
                        />
                        <Route
                          path="/richieste/:id/modulo-principale"
                          render={() => (
                            <SubmissionPrincipalModuleRedirector
                              submissionModules={submissionModules}
                              submissionId={submissionId}
                            />
                          )}
                        />
                        <Route
                          exact
                          path="/richieste/:id/pagamenti"
                          render={() => (
                            <SubmissionPaymentsCard
                              submissionId={submissionId!}
                              handleNavigation={handleNavigation}
                              submission={submission!}
                              readonly={isReadonlyPreview}
                            />
                          )}
                        />
                        <Route
                          exact
                          path="/richieste/:id/invia"
                          render={(props: any) => (
                            <CompilationSendCard
                              {...props}
                              handleNavigation={handleNavigation}
                              canSubmit={canSubmit}
                            />
                          )}
                        />
                      </Switch>
                    </EngineThemeProvider>
                  </FlexSubmissionRow>
                </ModuleFormSectionsMonitorContextProvider>
              </FormCoordinator>
            </ResponsiveCard>
          </ContentCardWrapper>
        </CardinalityConstraintContextProvider>
      </DelegationContextProvider>
    </PageLayout>
  );
}
